import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import {
    ArrowLeft, RefreshCw, Check, ChevronDown, MessageSquare,
    Image as ImageIcon, Settings, Search, X, BrainCircuit,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    fetchVeniceModels,
    fetchVeniceTraits,
    UnifiedModel,
    formatPrice,
    formatImagePrice,
} from '@/services/modelFetchService';
import { modelService } from '@/services/modelService';

// ─── Types ───────────────────────────────────────────────────────────────────

interface LLMSettings {
    textModel: string;
    fallbackTextModel: string;
    imageModel: string;
    fallbackImageModel: string;
}

const EMPTY_SETTINGS: LLMSettings = {
    textModel: '',
    fallbackTextModel: '',
    imageModel: '',
    fallbackImageModel: '',
};

// ─── Main component ──────────────────────────────────────────────────────────

const LLMSettingsPage: React.FC = () => {
    const { userProfile } = useAuth();
    const navigate = useNavigate();

    const [settings, setSettings] = useState<LLMSettings>(EMPTY_SETTINGS);
    const [draft, setDraft] = useState<LLMSettings>(EMPTY_SETTINGS);
    const [textModels, setTextModels] = useState<UnifiedModel[]>([]);
    const [imageModels, setImageModels] = useState<UnifiedModel[]>([]);
    const [traits, setTraits] = useState<Record<string, string>>({});

    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [fetchErrors, setFetchErrors] = useState<string[]>([]);

    const hasAdminAccess = userProfile?.role === 'admin';

    // ── Data fetching ──────────────────────────────────────────────────────

    const loadSettings = useCallback(async (): Promise<LLMSettings> => {
        const { data } = await supabase
            .from('app_settings')
            .select('value')
            .eq('key', 'llm_settings')
            .single();

        if (!data?.value) return EMPTY_SETTINGS;
        const saved = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        return {
            textModel: saved.textModel || saved.textGenerationModel || saved.studioGenerationModel || '',
            fallbackTextModel: saved.fallbackTextModel || '',
            imageModel: saved.imageModel || saved.imageGenerationModel || '',
            fallbackImageModel: saved.fallbackImageModel || '',
        };
    }, []);

    const refreshModels = useCallback(async () => {
        setIsRefreshing(true);
        setFetchErrors([]);
        const errors: string[] = [];

        const [text, image, traitsRes] = await Promise.all([
            fetchVeniceModels('text'),
            fetchVeniceModels('image'),
            fetchVeniceTraits('text'),
        ]);

        if (!text.success) errors.push(`Text models: ${text.error}`);
        if (!image.success) errors.push(`Image models: ${image.error}`);
        if (!traitsRes.success) errors.push(`Traits: ${traitsRes.error}`);

        setTextModels(text.models);
        setImageModels(image.models);
        setTraits(traitsRes.traits);
        setFetchErrors(errors);
        setIsRefreshing(false);
    }, []);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            const [, ] = await Promise.all([
                loadSettings().then((s) => { setSettings(s); setDraft(s); }),
                refreshModels(),
            ]);
            setIsLoading(false);
        };
        init();
    }, [loadSettings, refreshModels]);

    // ── Save ────────────────────────────────────────────────────────────────

    const saveSettings = async (overrides?: Partial<LLMSettings>) => {
        setIsSaving(true);
        setSaveMessage(null);

        const merged: LLMSettings = { ...settings, ...draft, ...(overrides || {}) };

        try {
            // Persist a single compact row (Venice-only — no provider fields).
            const { error } = await supabase
                .from('app_settings')
                .upsert({
                    key: 'llm_settings',
                    value: JSON.stringify(merged),
                    updated_at: new Date().toISOString(),
                });

            if (error) throw error;

            modelService.clearCache();
            setSettings(merged);
            setSaveMessage({ type: 'success', text: 'Saved' });
        } catch {
            setSaveMessage({ type: 'error', text: 'Failed to save' });
        } finally {
            setIsSaving(false);
            setTimeout(() => setSaveMessage(null), 2000);
        }
    };

    const isDirty =
        draft.textModel !== settings.textModel ||
        draft.fallbackTextModel !== settings.fallbackTextModel ||
        draft.imageModel !== settings.imageModel ||
        draft.fallbackImageModel !== settings.fallbackImageModel;

    const setField = (key: keyof LLMSettings, value: string) =>
        setDraft((prev) => ({ ...prev, [key]: value }));

    const quickPick = (type: 'text' | 'image', traitKey: string) => {
        const id = traits[traitKey];
        if (id) {
            const target = type === 'text' ? textModels : imageModels;
            if (target.some((m) => m.id === id)) setField(type === 'text' ? 'textModel' : 'imageModel', id);
        }
    };

    // ── Access control + loading ────────────────────────────────────────────

    if (!hasAdminAccess) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Admin Access Required</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Only administrators can configure AI models.</p>
                    <Button onClick={() => navigate('/manage')} variant="outline">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
        );
    }

    const liveText = textModels.find((m) => m.id === settings.textModel);
    const liveImage = imageModels.find((m) => m.id === settings.imageModel);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
            {saveMessage && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-sm font-medium text-white shadow-lg ${saveMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
                    {saveMessage.text}
                </div>
            )}

            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shadow-sm">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/manage?tab=dashboard')}>
                            <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Settings className="w-5 h-5" /> AI Model Settings
                        </h1>
                    </div>
                    <Button variant="outline" size="sm" onClick={refreshModels} disabled={isRefreshing}>
                        <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        <span className="ml-1.5">Refresh</span>
                    </Button>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 py-6">
                {fetchErrors.length > 0 && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                        {fetchErrors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                )}

                {/* Text models */}
                <ModelSection
                    title="Text Generation"
                    subtitle="Used for recipes, chat, modifications and analysis"
                    icon={<MessageSquare className="w-5 h-5 text-white" />}
                    tint="from-emerald-500 to-green-600"
                    primaryValue={draft.textModel}
                    onPrimaryChange={(id) => setField('textModel', id)}
                    fallbackValue={draft.fallbackTextModel}
                    onFallbackChange={(id) => setField('fallbackTextModel', id)}
                    models={textModels}
                    traits={traits}
                    onQuickPick={(k) => quickPick('text', k)}
                    active={liveText}
                />

                {/* Image models */}
                <ModelSection
                    title="Image Generation"
                    subtitle="Used for recipe photos"
                    icon={<ImageIcon className="w-5 h-5 text-white" />}
                    tint="from-orange-500 to-amber-600"
                    primaryValue={draft.imageModel}
                    onPrimaryChange={(id) => setField('imageModel', id)}
                    fallbackValue={draft.fallbackImageModel}
                    onFallbackChange={(id) => setField('fallbackImageModel', id)}
                    models={imageModels}
                    traits={traits}
                    onQuickPick={(k) => quickPick('image', k)}
                    active={liveImage}
                />

                {/* Save / Discard */}
                <div className="sticky bottom-0 -mx-4 px-4 py-3 mt-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur border-t border-gray-200 dark:border-slate-700 flex items-center justify-between gap-3">
                    <div className="text-xs">
                        {isDirty
                            ? <span className="text-amber-600 dark:text-amber-400 font-medium">Unsaved changes — not yet live</span>
                            : <span className="text-gray-500 dark:text-gray-400">{isSaving ? 'Saving…' : 'All changes saved'}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setDraft(settings); setSaveMessage(null); }} disabled={!isDirty || isSaving}>
                            Discard
                        </Button>
                        <Button size="sm" onClick={() => saveSettings()} disabled={!isDirty || isSaving}>
                            {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Section ────────────────────────────────────────────────────────────────

interface ModelSectionProps {
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    tint: string;
    primaryValue: string;
    onPrimaryChange: (id: string) => void;
    fallbackValue: string;
    onFallbackChange: (id: string) => void;
    models: UnifiedModel[];
    traits: Record<string, string>;
    onQuickPick: (key: string) => void;
    active?: UnifiedModel;
}

const ModelSection: React.FC<ModelSectionProps> = ({
    title, subtitle, icon, tint,
    primaryValue, onPrimaryChange, fallbackValue, onFallbackChange,
    models, traits, onQuickPick, active,
}) => {
    const model = models.find((m) => m.id === primaryValue);

    return (
        <section className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-slate-700 mb-4">
            <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tint} flex items-center justify-center`}>
                    {icon}
                </div>
                <div>
                    <h2 className="font-semibold text-gray-900 dark:text-white">{title}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
                </div>
            </div>

            <ModelSelect
                label="Model"
                models={models}
                value={primaryValue}
                onChange={onPrimaryChange}
                type={title.includes('Image') ? 'image' : 'text'}
            />

            {model && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {model.capabilities?.supportsReasoning && <Badge label="Reasoning" tone="violet" />}
                    {model.capabilities?.supportsVision && <Badge label="Vision" tone="emerald" />}
                    {model.capabilities?.supportsFunctionCalling && <Badge label="Function calling" tone="sky" />}
                    {model.capabilities?.supportsResponseSchema && <Badge label="Response schema" tone="amber" />}
                    <Badge label={`${model.contextLength?.toLocaleString() ?? '—'} ctx`} tone="gray" />
                    {(model.traits || []).slice(0, 3).map((t) => <Badge key={t} label={t} tone="gray" />)}
                </div>
            )}

            {/* Dynamic defaults */}
            {Object.keys(traits).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {(['default', 'most_intelligent', 'fastest'] as const)
                        .filter((k) => traits[k])
                        .map((k) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => onQuickPick(k)}
                                className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                            >
                                Use {k.replace('_', ' ')}
                            </button>
                        ))}
                </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fallback</div>
                <ModelSelect
                    label="Fallback model"
                    models={models}
                    value={fallbackValue}
                    onChange={onFallbackChange}
                    type={title.includes('Image') ? 'image' : 'text'}
                />
            </div>

            {active && <p className="mt-3 text-xs text-gray-400">Active: {active.name}</p>}
        </section>
    );
};

function Badge({ label, tone }: { label: string; tone: string }) {
    const tones: Record<string, string> = {
        violet: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
        emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
        sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
        amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
        gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    };
    return <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${tones[tone] || tones.gray}`}>{label}</span>;
}

// ─── Model select ────────────────────────────────────────────────────────────

interface ModelSelectProps {
    label: string;
    models: UnifiedModel[];
    value: string;
    onChange: (id: string) => void;
    type: 'text' | 'image';
}

const ModelSelect: React.FC<ModelSelectProps> = ({ label, models, value, onChange, type }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const selected = models.find((m) => m.id === value);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return models;
        return models.filter((m) => m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q));
    }, [models, query]);

    return (
        <div className="relative">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">{label}</div>
            <button
                type="button"
                onClick={() => setIsOpen((v) => !v)}
                className="w-full p-3 rounded-lg border-2 text-left transition-all flex items-center justify-between border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500"
            >
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">{selected?.name || value || 'Select a model…'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2">
                        <code className="truncate max-w-[220px]">{value}</code>
                        {selected && <span className="text-purple-600 dark:text-purple-400">{type === 'image' ? formatImagePrice(selected.pricing?.perImage) : `${formatPrice(selected.pricing?.input)} in / ${formatPrice(selected.pricing?.output)} out`}</span>}
                    </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
                    <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-600 overflow-hidden flex flex-col max-h-80">
                        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 p-2">
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                <input
                                    autoFocus
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={`Search ${models.length} models…`}
                                    className="w-full pl-8 pr-8 py-2 text-sm rounded-md bg-gray-50 dark:bg-slate-700/60 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-white"
                                />
                                {query && (
                                    <button type="button" onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="overflow-y-auto">
                            {filtered.length === 0 ? (
                                <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No models match "{query}"</div>
                            ) : (
                                filtered.map((m) => {
                                    const price = type === 'image'
                                        ? formatImagePrice(m.pricing?.perImage)
                                        : `${formatPrice(m.pricing?.input)} in · ${formatPrice(m.pricing?.output)} out`;
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => { onChange(m.id); setIsOpen(false); }}
                                            className={`w-full px-4 py-3 text-left flex items-center justify-between ${value === m.id ? 'bg-purple-50 dark:bg-purple-900/30' : 'hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-sm text-gray-900 dark:text-white truncate">{m.name}</span>
                                                    {m.capabilities?.supportsReasoning && <BrainCircuit className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                                                    <code className="truncate max-w-[180px]">{m.id}</code>
                                                    <span className="text-purple-600 dark:text-purple-400">{price}</span>
                                                </div>
                                            </div>
                                            {value === m.id && <Check className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LLMSettingsPage;